import moment, { lang, CALENDAR_FLOOR } from './calendarMoment.js'
import { element, parseArtistNames, eventDisplayTitle, resolveBooking } from './calendarHelpers.js'

const restUrl = window.php_vars?.restUrl

// DB tinyint columns arrive as "0"/"1" strings over REST.
const isTrue = (v) => v === 1 || v === '1' || v === true

const dayKey = (value) => (value || '').slice(0, 10)

// Shared across every calendar instance on the page (overlay + hero). Keyed by
// `year-month` (month 0-based, matching state.month). A month fetched by one
// instance — or seeded from the inline preload — warms the others, and revisiting
// a month never re-fetches. Data only; per-instance UI state (selection, which
// month is shown) lives in each controller, not here.
const monthCache = new Map()

// Earliest navigable month — the launch month. The site holds no events before it,
// so ← is dead here. Fixed date (see CALENDAR_FLOOR), shared with the week-stack.
const minMonth = moment(CALENDAR_FLOOR, 'YYYY-MM-DD').startOf('month')

/**
 * Wire one calendar root. The same shell renders in the header overlay and,
 * on the front page, inside the hero reveal — each gets an independent
 * controller (own state + DOM + listeners) so navigating one never moves the
 * other.
 */
const createCalendar = (root) => {
  const labels = (() => {
    try { return JSON.parse(root.dataset.i18n || '{}') } catch { return {} }
  })()

  const calendarHost = root.querySelector('[data-mini-calendar]')
  const lists = [...root.querySelectorAll('[data-mini-events]')]

  const today = moment().format('YYYY-MM-DD')

  const state = {
    year: moment().year(),
    month: moment().month(), // 0-based
    selected: today,
    dates: [],
  }

  const fetchMonth = async () => {
    const params = new URLSearchParams({ year: state.year, month: state.month + 1, lang })
    root.classList.add('is-loading')
    try {
      const r = await fetch(`${restUrl}glunz/v1/event-dates/month?${params}`)
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const data = await r.json()
      state.dates = Array.isArray(data?.dates) ? data.dates : []
    } catch {
      state.dates = []
    } finally {
      root.classList.remove('is-loading')
    }
  }

  const eventDaySet = () => {
    const set = new Set()
    for (const e of state.dates) set.add(dayKey(e.date_start))
    return set
  }

  // ── Mini calendar ────────────────────────────────────────────────────────
  const renderCalendar = () => {
    const eventDays = eventDaySet()
    const view = element('div', 'mini-calendar')

    // Header: prev / label / next
    const header = element('header', 'mini-calendar-header')
    const prev = element('button', 'mini-calendar-nav mini-calendar-prev', {
      type: 'button', 'aria-label': labels.prevMonth || 'Previous month',
    })
    prev.textContent = '←'
    // Dead once we're at (or before) the 6-month back cap — disable rather than no-op.
    prev.disabled = moment({ year: state.year, month: state.month }).isSameOrBefore(minMonth, 'month')
    prev.addEventListener('click', () => stepMonth(-1))
    const label = element('span', 'mini-calendar-label')
    label.textContent = moment({ year: state.year, month: state.month }).format('MMMM YYYY')
    const next = element('button', 'mini-calendar-nav mini-calendar-next', {
      type: 'button', 'aria-label': labels.nextMonth || 'Next month',
    })
    next.textContent = '→'
    next.addEventListener('click', () => stepMonth(1))
    header.append(prev, label, next)
    view.appendChild(header)

    // Weekday labels (Mo, Di, …), Monday-aligned
    const weekdays = element('div', 'mini-calendar-weekdays')
    const monday = moment().startOf('isoWeek')
    for (let i = 0; i < 7; i++) {
      const cell = element('div', 'mini-calendar-weekday')
      cell.textContent = monday.clone().add(i, 'days').format('dd')
      weekdays.appendChild(cell)
    }
    view.appendChild(weekdays)

    // Day grid, padded to whole ISO weeks; only this month's days are shown.
    const grid = element('div', 'mini-calendar-grid')
    const first = moment({ year: state.year, month: state.month, day: 1 })
    const cursor = first.clone().startOf('isoWeek')
    const gridEnd = first.clone().endOf('month').endOf('isoWeek')

    while (cursor.isSameOrBefore(gridEnd, 'day')) {
      if (cursor.month() !== state.month) {
        grid.appendChild(element('div', 'mini-calendar-day is-outside'))
      } else {
        const key = cursor.format('YYYY-MM-DD')
        const classes = ['mini-calendar-day']
        if (eventDays.has(key)) classes.push('has-events')
        if (key === today) classes.push('is-today')
        if (key === state.selected) classes.push('is-selected')
        const btn = element('button', classes.join(' '), { type: 'button', 'data-date': key })
        btn.textContent = cursor.format('D')
        btn.addEventListener('click', () => selectDay(key))
        grid.appendChild(btn)
      }
      cursor.add(1, 'days')
    }
    view.appendChild(grid)

    calendarHost.replaceChildren(view)
  }

  // ── Event lists ──────────────────────────────────────────────────────────
  const statusNode = (e) => {
    if (isTrue(e.is_cancelled)) {
      const s = element('span', 'mini-calendar-event-status mini-calendar-event-cancelled')
      s.textContent = labels.cancelled || ''
      return s
    }
    if (isTrue(e.is_sold_out)) {
      const s = element('span', 'mini-calendar-event-status mini-calendar-event-sold-out')
      s.textContent = e.sold_out_text
        || (e.sold_out_type === 'booked_out' ? labels.bookedOut : labels.soldOut)
        || ''
      return s
    }
    // Booking link vs. registration text — same three-way branch as the event
    // detail right column (template-parts/detail/event/right.php): a link when
    // there's a url, plain registration text ("ohne Anmeldung" etc.) when there's
    // only a label, nothing otherwise. Teaser context, so an `info` date with no
    // explicit link falls back to the event permalink (resolveBooking()).
    const { href: url, label, external } = resolveBooking(e, labels, e.permalink)

    if (url) {
      const attrs = { href: url }
      if (external) { attrs.target = '_blank'; attrs.rel = 'noopener noreferrer' }
      const a = element('a', 'mini-calendar-event-tickets', attrs)
      a.textContent = label || labels.book || ''
      if (external && labels.newWindow) {
        const hidden = element('span', 'hidden')
        hidden.textContent = ` ${labels.newWindow}`
        a.appendChild(hidden)
      }
      return a
    }

    if (label) {
      const s = element('span', 'mini-calendar-event-registration')
      s.textContent = label
      return s
    }

    return null
  }

  const renderEvent = (e, dayEvents = []) => {
    const cls = ['mini-calendar-event']
    if (isTrue(e.is_cancelled)) cls.push('is-cancelled')
    if (isTrue(e.is_sold_out)) cls.push('is-sold-out')
    const item = element('div', cls.join(' '), {
      'data-post-id': e.post_id,
    })

    const top = element('div', 'mini-calendar-event-top')
    const start = moment(e.date_start)
    const end = e.date_end ? moment(e.date_end) : null
    const time = element('time', 'mini-calendar-event-time', {
      datetime: (e.date_start || '').replace(' ', 'T'),
    })
    time.textContent = end && !end.isSame(start)
      ? `${start.format('HH:mm')} – ${end.format('HH:mm')}`
      : start.format('HH:mm')
    top.appendChild(time)
    const status = statusNode(e)
    if (status) top.appendChild(status)
    item.appendChild(top)

    const bottomTag = e.permalink ? 'a' : 'div'
    const bottomAttrs = e.permalink ? { href: e.permalink } : {}
    const bottom = element(bottomTag, 'mini-calendar-event-bottom', bottomAttrs)
    const title = element('div', 'mini-calendar-event-title')
    title.textContent = eventDisplayTitle(e, dayEvents)
    bottom.appendChild(title)
    const artists = parseArtistNames(e.artist_names)
    if (artists.length) {
      const a = element('div', 'mini-calendar-event-artists')
      a.textContent = artists.join(', ')
      bottom.appendChild(a)
    }
    item.appendChild(bottom)

    return item
  }

  const renderLists = () => {
    // Collision detection (semester suffix) is per selected day, across all
    // event-type groups — not just the current container's filtered slice.
    const dayEvents = state.dates.filter(e => dayKey(e.date_start) === state.selected)
    for (const container of lists) {
      const slugs = (container.dataset.miniEvents || '').split(',').map(s => s.trim()).filter(Boolean)
      const events = dayEvents.filter(e =>
        slugs.some(s => (e.event_types || '').includes(`|${s}|`))
      )

      container.replaceChildren(...events.map(e => renderEvent(e, dayEvents)))

      const empty = container.parentElement.querySelector('[data-mini-empty]')
      if (empty) empty.hidden = events.length > 0
    }
  }

  const render = () => {
    renderCalendar()
    renderLists()
  }

  const selectDay = (key) => {
    state.selected = key
    render()
  }

  const stepMonth = async (delta) => {
    const m = moment({ year: state.year, month: state.month }).add(delta, 'month')
    if (m.isBefore(minMonth, 'month')) return // before the launch floor (July 2026)
    state.year = m.year()
    state.month = m.month()
    // Keep today selected when we're back on the real current month, otherwise
    // default to the 1st of the shown month.
    state.selected = m.isSame(moment(), 'month') ? today : m.clone().startOf('month').format('YYYY-MM-DD')

    const key = `${state.year}-${state.month}`
    if (monthCache.has(key)) {
      state.dates = monthCache.get(key)
    } else {
      await fetchMonth()
      monthCache.set(key, state.dates)
    }
    render()
  }

  // Render synchronously from the server-inlined current month → no fetch, no
  // blink. The overlay is built up front while hidden, so opening it is instant.
  const preload = (() => {
    try { return JSON.parse(root.dataset.preload || 'null') } catch { return null }
  })()

  if (preload && Array.isArray(preload.dates)) {
    state.dates = preload.dates
    monthCache.set(`${preload.year}-${preload.month - 1}`, preload.dates) // payload month is 1-based
    render()
  } else {
    // Cold/missing preload (stale cached HTML, build failure) — fall back to a
    // fetch so the calendar is never left empty.
    fetchMonth().then(() => {
      monthCache.set(`${state.year}-${state.month}`, state.dates)
      render()
    })
  }
}

document.querySelectorAll('[data-calendar-overlay]').forEach(createCalendar)
